import {
  LayoutDashboard,
  List,
  Clock,
  FileEdit,
  Users,
  History,
  Settings,
  LogOut,
  User,
  ChevronsUpDown,
  Menu,
  Table2,
  ArrowDownUp,
  type LucideIcon,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils/cn'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { supabase } from '@/lib/supabase/client'
import type { ProjectRole } from '@/features/auth/context/project-role-context'

interface NavItem {
  key: string
  label: string
  icon: LucideIcon
  path: string
  roles: ProjectRole[]
}

function buildNavItems(projectId: string): NavItem[] {
  const all: ProjectRole[] = ['super_user', 'admin', 'approver', 'data_entry', 'viewer']
  const adminOnly: ProjectRole[] = ['super_user', 'admin']
  const approverUp: ProjectRole[] = ['super_user', 'admin', 'approver']
  const dataEntryUp: ProjectRole[] = ['super_user', 'admin', 'data_entry']

  return [
    { key: 'overview', label: 'نمای کلی', icon: LayoutDashboard, path: `/projects/${projectId}/overview`, roles: all },
    { key: 'records', label: 'سوابق', icon: List, path: `/projects/${projectId}/records`, roles: all },
    { key: 'explore', label: 'کاوش داده‌ها', icon: Table2, path: `/projects/${projectId}/explore`, roles: all },
    { key: 'data-io', label: 'ورود/خروجی داده', icon: ArrowDownUp, path: `/projects/${projectId}/data-io`, roles: dataEntryUp },
    { key: 'pending', label: 'در انتظار بررسی', icon: Clock, path: `/projects/${projectId}/pending-review`, roles: approverUp },
    { key: 'form-builder', label: 'فرم‌ساز', icon: FileEdit, path: `/projects/${projectId}/form-builder`, roles: adminOnly },
    { key: 'members', label: 'اعضا', icon: Users, path: `/projects/${projectId}/members`, roles: adminOnly },
    { key: 'audit', label: 'تاریخچه', icon: History, path: `/projects/${projectId}/audit`, roles: all },
    { key: 'settings', label: 'تنظیمات', icon: Settings, path: `/projects/${projectId}/settings`, roles: adminOnly },
  ]
}

interface SidebarUserProps {
  name: string
  email: string
  initials: string
}

interface SidebarProps {
  activeKey: string
  collapsed?: boolean
  onToggle?: () => void
  projectId?: string
  role: ProjectRole
  counts?: Partial<Record<string, number>>
  user?: SidebarUserProps
}

export function Sidebar({
  activeKey,
  collapsed = false,
  onToggle,
  projectId,
  role,
  counts = {},
  user,
}: SidebarProps) {
  const navigate = useNavigate()
  const signOut = useAuthStore((s) => s.signOut)

  const items = projectId
    ? buildNavItems(projectId).filter((item) => role === null || item.roles.includes(role))
    : []

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    signOut()
    navigate('/sign-in')
  }

  return (
    <aside
      className={cn(
        'h-full bg-card border-e border-border flex flex-col py-3 transition-all duration-200 shrink-0',
        collapsed ? 'w-14' : 'w-56'
      )}
    >
      {/* Hamburger toggle */}
      <div className="px-2 pb-1 shrink-0">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title={collapsed ? 'باز کردن منو (Ctrl+B)' : 'بستن منو (Ctrl+B)'}
        >
          <Menu className="w-4 h-4" />
        </button>
      </div>

      <nav className="flex-1 px-2 space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon
          const isActive = activeKey === item.key
          const count = counts[item.key]

          return (
            <Link
              key={item.key}
              to={item.path}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors group',
                isActive
                  ? 'bg-primary/15 text-foreground border-s-2 border-primary ps-[9px]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted border-s-2 border-transparent ps-[9px]'
              )}
            >
              <Icon
                className={cn(
                  'w-4 h-4 shrink-0',
                  isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                )}
              />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate font-medium">{item.label}</span>
                  {count !== undefined && count > 0 && (
                    <Badge
                      className={cn(
                        'h-4 min-w-4 px-1 text-[10px] leading-none',
                        item.key === 'pending'
                          ? 'bg-destructive/20 text-red-400 border-0'
                          : 'bg-muted text-muted-foreground border-0'
                      )}
                    >
                      {count}
                    </Badge>
                  )}
                </>
              )}
            </Link>
          )
        })}
      </nav>

      {user && (
        <div className="px-2 pt-2 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                'w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-muted transition-colors text-start',
              )}>
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-medium">
                    {user.initials}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate leading-none mb-0.5">
                        {user.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate leading-none">
                        {user.email}
                      </p>
                    </div>
                    <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-52">
              <DropdownMenuItem onClick={() => navigate('/account')}>
                <User className="w-4 h-4 me-2" />
                حساب کاربری
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-4 h-4 me-2" />
                خروج
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </aside>
  )
}
